/**
 * useSales Hook
 * 영업 관리 관련 커스텀 훅
 */

import { useState, useCallback, useEffect } from 'react';
import { SalesRepository } from '../repositories/SalesRepository';
import { SalesService } from '../services/SalesService';
import {
  SalesLead,
  SalesActivity,
  SalesStatus,
  ActivityType,
  SalesStatistics,
  CreateLeadRequest,
  UpdateLeadRequest,
} from '../models/SalesLead';
import { ServiceType } from '../models';

const salesRepository = new SalesRepository();
const salesService = new SalesService(salesRepository);

export const useSales = (serviceType?: ServiceType) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<SalesLead[]>([]);
  const [selectedLead, setSelectedLead] = useState<SalesLead | null>(null);
  const [activities, setActivities] = useState<SalesActivity[]>([]);
  const [statistics, setStatistics] = useState<SalesStatistics | null>(null);
  const [consultingCount, setConsultingCount] = useState(0);

  // ============================================
  // 목록 조회
  // ============================================

  /**
   * 전체 영업 리드 조회
   */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getAllLeads(serviceType);
      setLeads(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 신규문의 영업 리드 조회
   */
  const fetchNewInquiry = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getNewInquiryLeads(serviceType);
      setLeads(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 상담중 영업 리드 조회
   */
  const fetchConsulting = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getConsultingLeads(serviceType);
      setLeads(data);
      setConsultingCount(data.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 계약완료 영업 리드 조회
   */
  const fetchContracted = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getContractedLeads(serviceType);
      setLeads(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 내가 담당하는 영업 리드 조회
   */
  const fetchMyLeads = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getMyLeads();
      setLeads(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 통계 조회
   */
  const fetchStatistics = useCallback(async () => {
    try {
      const data = await salesService.getStatistics();
      setStatistics(data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  }, []);

  /**
   * 상담 중인 영업 건수 갱신
   */
  const refreshConsultingCount = useCallback(async () => {
    try {
      const count = await salesService.getConsultingCount(serviceType);
      setConsultingCount(count);
    } catch (err) {
      console.error('Failed to refresh consulting count:', err);
    }
  }, [serviceType]);

  // ============================================
  // 상세 조회
  // ============================================

  /**
   * 영업 리드 상세 조회
   */
  const fetchLead = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.getLead(id);
      setSelectedLead(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 활동 목록 조회
   */
  const fetchActivities = useCallback(async (leadId: string) => {
    try {
      const data = await salesService.getActivities(leadId);
      setActivities(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      return [];
    }
  }, []);

  // ============================================
  // 생성/수정
  // ============================================

  /**
   * 영업 리드 생성
   */
  const createLead = useCallback(
    async (request: CreateLeadRequest): Promise<SalesLead | null> => {
      setLoading(true);
      setError(null);

      try {
        const lead = await salesService.createLead(request);
        await fetchConsulting();
        return lead;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '생성에 실패했습니다.';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchConsulting]
  );

  /**
   * 문의에서 영업 리드 생성
   */
  const createFromContact = useCallback(
    async (contactId: string): Promise<SalesLead | null> => {
      setLoading(true);
      setError(null);

      try {
        const lead = await salesService.createLeadFromContact(contactId);
        await fetchConsulting();
        return lead;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '전환에 실패했습니다.';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchConsulting]
  );

  /**
   * 영업 리드 정보 수정
   */
  const updateLead = useCallback(
    async (id: string, request: UpdateLeadRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const updated = await salesService.updateLead(id, request);
        setSelectedLead(updated);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '수정에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 상태 변경
   */
  const updateStatus = useCallback(
    async (id: string, status: SalesStatus, reason?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const updated = await salesService.updateStatus(id, status, reason);
        setSelectedLead(updated);
        await fetchConsulting();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '상태 변경에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchConsulting]
  );

  /**
   * 담당자 할당
   */
  const assignLead = useCallback(
    async (id: string, assigneeId: string, assigneeName: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const updated = await salesService.assignLead(id, assigneeId, assigneeName);
        setSelectedLead(updated);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '담당자 할당에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 계약 완료 처리
   */
  const completeContract = useCallback(
    async (id: string, organizationId: string, contractAmount?: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const updated = await salesService.completeContract(id, organizationId, contractAmount);
        setSelectedLead(updated);
        await fetchConsulting();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '계약 완료 처리에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchConsulting]
  );

  // ============================================
  // 활동
  // ============================================

  /**
   * 활동 추가
   */
  const addActivity = useCallback(
    async (leadId: string, activityType: ActivityType, content: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await salesService.addActivity(leadId, activityType, content);
        await fetchActivities(leadId);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '활동 추가에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchActivities]
  );

  // ============================================
  // 검색
  // ============================================

  /**
   * 서버 사이드 검색
   */
  const search = useCallback(async (query: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await salesService.searchLeads(query);
      setLeads(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '검색에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 클라이언트 사이드 필터링
   */
  const filterLeads = useCallback(
    (query: string) => {
      const filtered = salesService.filterLeads(leads, query);
      setLeads(filtered);
    },
    [leads]
  );

  // ============================================
  // 초기화
  // ============================================

  // 마운트 시 상담 건수 조회
  useEffect(() => {
    refreshConsultingCount();
  }, [refreshConsultingCount]);

  // ============================================
  // Clear Functions
  // ============================================

  const clearSelectedLead = useCallback(() => {
    setSelectedLead(null);
    setActivities([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    leads,
    selectedLead,
    activities,
    statistics,
    consultingCount,
    loading,
    error,

    // Fetch
    fetchAll,
    fetchNewInquiry,
    fetchConsulting,
    fetchContracted,
    fetchMyLeads,
    fetchLead,
    fetchActivities,
    fetchStatistics,
    refreshConsultingCount,

    // Create/Update
    createLead,
    createFromContact,
    updateLead,
    updateStatus,
    assignLead,
    completeContract,

    // Activity
    addActivity,

    // Search
    search,
    filterLeads,

    // Clear
    clearSelectedLead,
    clearError,
    setSelectedLead,
  };
};
